import Layout from "../../components/Layout.tsx";
import { Dialog, DialogTitle } from "../../components/Dialog.tsx";
import { Button } from "../../components/Button.tsx";
import { useLocation } from "wouter";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/Table.tsx";
import { RiSearchLine, RiLoopRightLine, RiLoopLeftLine, RiArrowRightLine, RiTimeLine } from "@remixicon/react";
import dayjs from "dayjs";
import { FieldGroup } from "../../components/Fieldset.tsx";
import { Input, InputGroup } from "../../components/Input.tsx";
import { Badge } from "../../components/Badge.tsx";
import { getSyncData } from "../../hooks/data.ts";
import { Connection, RetriggerPreview, RunDetails } from "../../types.ts";
import { useState, useMemo, useEffect } from "react";
import Fuse from "fuse.js";
import toast from "react-hot-toast";
import { useUser } from "../../context/UserContext.tsx";
import { ApiError, fetchRetriggerPreview, retriggerRun, fetchConnections } from "../../services/ApiService.ts";
import { getResultBadge, getRunStateColor, getRunStateLabel, MODULE_LABELS, parseUtcDate } from "../../utils.ts";
import LoadingState from "../../components/LoadingState.tsx";
import { isReadOnly } from "../../config.ts";

const ResultBadge = ({ run }: { run: RunDetails }) => {
  const badge = getResultBadge(run);
  return <Badge color={badge.color} title={badge.title}>{badge.label}</Badge>;
};

interface UpcomingRun {
  connectionId: number;
  connectionName: string;
  destinationSystem: string;
  dryRun: boolean;
  nextRunAt: string;
}

const SyncOverview = () => {
  const { user } = useUser();
  const { data: syncData, loading: syncDataLoading } = getSyncData(1000, 5000);
  const [searchInput, setSearchInput] = useState<string>("");
  const [filter, setFilter] = useState<"all" | "syncs" | "dryruns">("all");
  const [connections, setConnections] = useState<Connection[] | null>(null);
  const [connectionsError, setConnectionsError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.administrationCodes?.length) return;
    setConnectionsError(null);
    fetchConnections()
      .then(({ data, error }) => {
        if (error) {
          setConnectionsError(error);
          setConnections([]);
          return;
        }
        setConnections(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        setConnectionsError(
          error instanceof ApiError ? error.message : "Koppelingen konden niet worden geladen.",
        );
        setConnections([]);
      });
  }, [user?.administrationCodes]);

  const hasConnections = connections === null ? null : connectionsError !== null || connections.length > 0;

  if (!user?.administrationCodes?.length) {
    return (
      <Layout
        title="Synchronisaties"
        description=""
        stepTitle=""
        showBackButton={false}
        showNextButton={false}
      >
        <p className="text-sm text-gray-500">Geen administratie beschikbaar.</p>
      </Layout>
    );
  }

  return (
    <Layout
      title="Synchronisaties"
      description="Bekijk hier de status van alle synchronisaties"
      stepTitle=""
      showBackButton={false}
      showNextButton={false}
    >
      {hasConnections === null || syncDataLoading ? (
        <LoadingState />
      ) : hasConnections === false ? (
        <div className="flex flex-col items-center justify-center py-16 px-6">
          <div className="rounded-full bg-blue-50 p-4 mb-6">
            <RiLoopLeftLine className="w-8 h-8 text-blue-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Hier vind je straks al je synchronisaties
          </h3>
          <p className="text-sm text-gray-500 text-center max-w-md mb-8">
            Zodra je een koppeling hebt ingesteld tussen Easyflex en Exact Online,
            verschijnen hier al je synchronisaties.
          </p>
          <Button color="primary" {...(isReadOnly ? { disabled: true } : { href: "/nieuwe-koppeling" })}>
            Koppeling instellen
            <RiArrowRightLine className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <FieldGroup>
          {connectionsError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {connectionsError}
            </div>
          )}
          {/* Filter tabs + search */}
          <div className="flex items-center gap-6 mb-2">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {([
                { key: "all", label: "Alles" },
                { key: "syncs", label: "Synchronisaties" },
                { key: "dryruns", label: "Proefdraai" },
              ] as const).map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setFilter(tab.key)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${filter === tab.key
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                    }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <InputGroup>
              <RiSearchLine data-slot="icon" />
              <Input
                name="search"
                placeholder="Zoek&hellip;"
                aria-label="Zoeken"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </InputGroup>
          </div>
          <SyncTable searchInput={searchInput} syncData={syncData} connections={connections ?? []} filter={filter} />
        </FieldGroup>
      )}
    </Layout>
  );
};


const SyncTable = ({ searchInput, syncData, connections = [], filter }: {
  searchInput?: string;
  syncData: RunDetails[];
  connections?: Connection[];
  filter?: "all" | "syncs" | "dryruns";
}) => {
  const [, navigate] = useLocation();
  const [retriggerModalOpen, setRetriggerModalOpen] = useState(false);
  const [runToRetrigger, setRunToRetrigger] = useState<RunDetails | null>(null);
  const [retriggering, setRetriggering] = useState(false);
  const [retriggerPreview, setRetriggerPreview] = useState<RetriggerPreview | null>(null);
  const [retriggerPreviewLoading, setRetriggerPreviewLoading] = useState(false);
  const [retriggerPreviewError, setRetriggerPreviewError] = useState<string | null>(null);

  const handleRetriggerClick = (run: RunDetails, e: React.MouseEvent) => {
    e.stopPropagation();
    setRunToRetrigger(run);
    setRetriggerModalOpen(true);
  };

  useEffect(() => {
    if (!retriggerModalOpen || !runToRetrigger) {
      setRetriggerPreview(null);
      setRetriggerPreviewError(null);
      setRetriggerPreviewLoading(false);
      return;
    }

    let cancelled = false;

    setRetriggerPreview(null);
    setRetriggerPreviewError(null);
    setRetriggerPreviewLoading(true);

    fetchRetriggerPreview(runToRetrigger.id)
      .then((preview) => {
        if (!cancelled) {
          setRetriggerPreview(preview);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setRetriggerPreviewError(
            error instanceof ApiError ? error.message : "Kon het voorbeeld niet laden.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setRetriggerPreviewLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [retriggerModalOpen, runToRetrigger]);

  const handleConfirmRetrigger = async () => {
    if (!runToRetrigger) return;

    setRetriggering(true);
    try {
      const { error } = await retriggerRun(runToRetrigger.id);
      if (error) {
        toast.error(error);
      } else {
        toast.success("Synchronisatie gestart.");
      }
    } catch {
      toast.error("Er is een fout opgetreden bij het starten van de synchronisatie.");
    } finally {
      setRetriggering(false);
      setRetriggerModalOpen(false);
      setRunToRetrigger(null);
    }
  };


  const upcomingRuns = useMemo<UpcomingRun[]>(
    () =>
      connections
        .filter((c) => c.id && c.synchronisationSchema && !c.synchronisationSchema.isPaused && c.synchronisationSchema.nextRunAt)
        .map((c) => ({
          connectionId: c.id!,
          connectionName: c.name,
          destinationSystem: c.destinationSystem ?? "",
          dryRun: c.synchronisationSchema!.dryRun,
          nextRunAt: c.synchronisationSchema!.nextRunAt!,
        }))
        .sort((a, b) => parseUtcDate(b.nextRunAt).getTime() - parseUtcDate(a.nextRunAt).getTime()),
    [connections],
  );

  const applyFilters = <T extends { dryRun: boolean; connectionName: string; destinationSystem: string }>(data: T[]) => {
    let filtered = data;
    if (filter === "syncs") {
      filtered = filtered.filter((r) => !r.dryRun);
    } else if (filter === "dryruns") {
      filtered = filtered.filter((r) => r.dryRun);
    }

    if (!searchInput) return filtered;
    const fuseInstance = new Fuse(filtered, {
      keys: ["connectionName", "destinationSystem"],
      threshold: 0.3,
    });
    return fuseInstance.search(searchInput).map((result) => result.item);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- listing this would re-run the effect on changes it should ignore
  const filteredUpcoming = useMemo(() => applyFilters(upcomingRuns), [searchInput, upcomingRuns, filter]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- listing this would re-run the effect on changes it should ignore
  const filteredData = useMemo(() => applyFilters(syncData), [searchInput, syncData, filter]);

  const formatDate = (dateString: string) => {
    const utcString = dateString.endsWith('Z') || dateString.includes('+') ? dateString : dateString + 'Z';
    return dayjs(utcString).format('D/M/YYYY - HH:mm');
  };

  const getRetriggerSourceLabel = (source: "cursor" | "start_boundary") => {
    if (source === "start_boundary") {
      return "Startgrens uit eerdere run";
    }
    return "Laatst gesynchroniseerd";
  };

  const handleRowClick = (run: RunDetails) => {
    if (run.dryRun && run.state === "COMPLETED" && run.result !== "FAILED") {
      navigate(`/proef-synchronisatie/${run.id}`);
    } else {
      navigate(`/synchronisatie/${run.id}`);
    }
  };

  return (
    <>
      <Table dense className="rounded">
        <TableHead className="bg-gray-200">
          <TableRow>
            <TableHeader></TableHeader>
            <TableHeader>Koppeling</TableHeader>
            <TableHeader>Systeem</TableHeader>
            <TableHeader>Status</TableHeader>
            <TableHeader>Resultaat</TableHeader>
            <TableHeader>Tijdstip</TableHeader>
            <TableHeader className="w-12"></TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredUpcoming.map((upcoming, index) => (
            <TableRow
              key={`upcoming-${upcoming.connectionId}`}
              className={`${isReadOnly ? "" : "cursor-pointer hover:bg-gray-100"} ${upcoming.dryRun ? "border-l-4 border-yellow-400" : ""} ${index === filteredUpcoming.length - 1 && filteredData.length > 0 ? "[&>td]:border-b-2 [&>td]:border-b-zinc-950/20" : ""}`}
              onClick={isReadOnly ? undefined : () => navigate(`/koppeling/${upcoming.connectionId}`)}
            >
              <TableCell />
              <TableCell>
                {upcoming.connectionName}
                {upcoming.dryRun && (
                  <span className="ml-2 px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                    Proefdraai
                  </span>
                )}
              </TableCell>
              <TableCell>{upcoming.destinationSystem}</TableCell>
              <TableCell>
                <Badge color="zinc">
                  <RiTimeLine className="w-3.5 h-3.5" />
                  Gepland
                </Badge>
              </TableCell>
              <TableCell className="text-gray-400">—</TableCell>
              <TableCell>{formatDate(upcoming.nextRunAt)}</TableCell>
              <TableCell />
            </TableRow>
          ))}
          {filteredData.map((run, index) => (
            <TableRow
              key={run.id}
              className={`cursor-pointer hover:bg-gray-100 ${run.dryRun ? "border-l-4 border-yellow-400" : ""}`}
              onClick={() => handleRowClick(run)}
            >
              <TableCell className="text-gray-400 text-xs">{index + 1}</TableCell>
              <TableCell>
                {run.connectionName}
                {run.dryRun && (
                  <span className="ml-2 px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                    Proefdraai
                  </span>
                )}
              </TableCell>
              <TableCell>{run.destinationSystem}</TableCell>
              <TableCell>
                <Badge color={getRunStateColor(run.state)}>
                  {getRunStateLabel(run.state)}
                </Badge>
              </TableCell>
              <TableCell>
                {run.state === "RUNNING" || run.state === "SCHEDULED" ? (
                  <span className="inline-block w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
                ) : run.dryRun && run.state === "COMPLETED" && run.result !== "FAILED" && run.result !== "CANCELLED" ? (
                  run.dryRunConfirmed ? (
                    <Badge color="green">Bevestigd</Badge>
                  ) : (
                    <span className="text-blue-600 text-sm font-medium">Bekijk data</span>
                  )
                ) : (
                  <ResultBadge run={run} />
                )}
              </TableCell>
              <TableCell>{formatDate(run.runDatetime)}</TableCell>
              <TableCell>
                {!run.dryRun && (
                  <button
                    onClick={(e) => handleRetriggerClick(run, e)}
                    disabled={isReadOnly}
                    className="p-1 rounded hover:bg-gray-200 text-gray-600 hover:text-gray-900 disabled:pointer-events-none disabled:opacity-50"
                    title="Opnieuw synchroniseren"
                  >
                    <RiLoopRightLine className="w-4 h-4" />
                  </button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Retrigger confirmation modal */}
      <Dialog
        open={retriggerModalOpen && runToRetrigger !== null}
        onClose={() => setRetriggerModalOpen(false)}
      >
        <DialogTitle className="text-lg font-semibold text-gray-900">
          Synchronisatie bevestigen
        </DialogTitle>
        {runToRetrigger && (
          <>
            <p className="mt-3 text-sm text-gray-600">
              Weet je zeker dat je de synchronisatie{" "}
              <strong>{runToRetrigger.connectionName}</strong> —{" "}
              {runToRetrigger.destinationSystem} opnieuw wilt uitvoeren?
            </p>
            <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50/80 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-gray-900">Startpunt</p>
                  <p className="text-xs text-gray-500">Per module zie je vanaf welke grens de synchronisatie opnieuw start.</p>
                </div>
                {retriggerPreviewLoading && (
                  <span className="inline-block h-4 w-4 rounded-full border-2 border-gray-300 border-t-blue-600 animate-spin" />
                )}
              </div>

              {retriggerPreviewError && (
                <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {retriggerPreviewError}
                </div>
              )}

              {!retriggerPreviewError && !retriggerPreviewLoading && retriggerPreview && (
                retriggerPreview.modules.length > 0 ? (
                  <div className="mt-3 space-y-2">
                    {retriggerPreview.modules.map((modulePreview) => (
                      <div
                        key={modulePreview.module}
                        className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 bg-white px-3 py-2"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            {MODULE_LABELS[modulePreview.module] || modulePreview.module}
                          </p>
                          <p className="text-xs text-gray-500">
                            {getRetriggerSourceLabel(modulePreview.querySource)}
                          </p>
                        </div>
                        <p className="shrink-0 text-sm font-medium tabular-nums text-gray-900">
                          {formatDate(modulePreview.queryFromChangedAt)}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-gray-500">Geen boekingsmodules actief voor deze koppeling.</p>
                )
              )}
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <button
                onClick={() => setRetriggerModalOpen(false)}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                disabled={retriggering}
              >
                Annuleren
              </button>
              <Button
                color="primary"
                onClick={handleConfirmRetrigger}
                disabled={retriggering || retriggerPreviewLoading || !!retriggerPreviewError}
              >
                {retriggering ? "Bezig..." : "Ja, synchroniseer nu"}
              </Button>
            </div>
          </>
        )}
      </Dialog>
    </>
  );
};

export default SyncOverview;
export { SyncTable };
